import React, { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  Select,
  Tag,
  Divider,
  Space,
  Descriptions,
  Collapse,
  Checkbox,
  message,
  Button,
  Switch
} from 'antd'
import {
  ROLES,
  ROLE_NAMES,
  ROLE_DESCRIPTIONS,
  ROLE_COLORS,
  ACCOUNT_STATUS,
  ACCOUNT_STATUS_NAMES,
  PERMISSIONS_MAP,
  groupPermissionsByModule
} from '../utils/permissionUtils'
import {
  getRoles,
  updateEmployeeRole,
  updateEmployeeStatus
} from '../api/permission'
import styled from 'styled-components'
import Hint from './common/Hint'

const { Option } = Select
const { Panel } = Collapse

const RoleTag = styled(Tag)`
  margin: 4px;
  padding: 4px 12px;
  font-size: 14px;
`

const PermissionSection = styled.div`
  margin: 16px 0;
`

const PermissionItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`

/**
 * 角色管理彈窗
 */
const RoleManagementModal = ({
  visible,
  employee,
  businessId,
  currentUserRole,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [roles, setRoles] = useState([])
  const [selectedRole, setSelectedRole] = useState(null)
  const [customPermissions, setCustomPermissions] = useState([])
  const [deniedPermissions, setDeniedPermissions] = useState([])
  const [accountStatus, setAccountStatus] = useState('active')
  const [showAdvanced, setShowAdvanced] = useState(false)

  useEffect(() => {
    if (visible && businessId) {
      fetchRoles()
    }
  }, [visible, businessId])

  useEffect(() => {
    if (employee) {
      console.log('=== 初始化员工权限数据 ===')
      console.log('员工数据:', employee)
      console.log('员工 customPermissions:', employee.customPermissions)
      console.log('员工 deniedPermissions:', employee.deniedPermissions)
      console.log('员工 businessRole:', employee.businessRole)

      setSelectedRole(employee.businessRole)
      setCustomPermissions(employee.customPermissions || [])
      setDeniedPermissions(employee.deniedPermissions || [])
      setAccountStatus(employee.accountStatus || 'active')
      form.setFieldsValue({
        businessRole: employee.businessRole,
        accountStatus: employee.accountStatus || 'active'
      })

      console.log('初始化后的状态:')
      console.log('- selectedRole:', employee.businessRole)
      console.log('- customPermissions:', employee.customPermissions || [])
      console.log('- deniedPermissions:', employee.deniedPermissions || [])
      console.log('========================')
    }
  }, [employee, form])

  // 追踪 customPermissions 的变化
  useEffect(() => {
    console.log('=== customPermissions 状态变化 ===')
    console.log('新的 customPermissions:', customPermissions)
    console.log(
      '是否包含 finance:view:',
      customPermissions.includes('finance:view')
    )
    console.log(
      '是否包含 finance:manage:',
      customPermissions.includes('finance:manage')
    )
    console.log('==================================')
  }, [customPermissions])

  const fetchRoles = async () => {
    try {
      const response = await getRoles(businessId)
      if (response.data.success) {
        setRoles(response.data.data)
      }
    } catch (error) {
      message.error('獲取角色列表失敗')
    }
  }

  const handleRoleChange = (value) => {
    setSelectedRole(value)
    const role = roles.find((r) => r.role === value)
    if (role) {
      // 当角色改变时，清理掉那些现在属于角色默认权限的 customPermissions
      const rolePermissions = role.permissions || []
      const hasAllPermissions = rolePermissions.includes('*')

      if (hasAllPermissions) {
        // 如果角色有所有权限，清空自定义权限
        setCustomPermissions([])
      } else {
        // 移除那些现在属于角色默认权限的自定义权限
        setCustomPermissions((prev) =>
          prev.filter((perm) => !rolePermissions.includes(perm))
        )
      }

      // 清空 deniedPermissions，因为角色已经改变了
      setDeniedPermissions([])
    }
  }

  const handleSubmit = async () => {
    try {
      await form.validateFields()
      setLoading(true)

      const values = form.getFieldsValue()

      // 获取当前角色的默认权限
      const currentRole = roles.find((r) => r.role === values.businessRole)
      const rolePermissions = currentRole?.permissions || []
      const hasAllPermissions = rolePermissions.includes('*')

      // 过滤自定义权限：排除角色默认权限中已包含的权限
      let finalCustomPermissions = [...customPermissions]
      if (!hasAllPermissions) {
        finalCustomPermissions = customPermissions.filter(
          (perm) => !rolePermissions.includes(perm)
        )
      } else {
        // 如果角色有所有权限，不应该有自定义权限
        finalCustomPermissions = []
      }

      // 过滤禁用权限：只保留当前角色默认权限中存在的权限
      let finalDeniedPermissions = [...deniedPermissions]
      if (!hasAllPermissions) {
        finalDeniedPermissions = deniedPermissions.filter((perm) =>
          rolePermissions.includes(perm)
        )
      } else {
        // 如果角色有所有权限，可以禁用任何权限
        // 保持原样
      }

      // 调试信息：打印提交的数据
      console.log('=== 角色权限提交调试信息 ===')
      console.log('1. 当前 customPermissions 状态:', customPermissions)
      console.log('2. 当前 deniedPermissions 状态:', deniedPermissions)
      console.log('3. 选择的角色:', values.businessRole)
      console.log('4. 角色默认权限:', rolePermissions)
      console.log('5. 角色是否有所有权限 (*):', hasAllPermissions)

      // 再次确认：检查 customPermissions 中是否包含财务权限
      const hasFinanceView = customPermissions.includes('finance:view')
      const hasFinanceManage = customPermissions.includes('finance:manage')
      console.log('6. customPermissions 是否包含 finance:view:', hasFinanceView)
      console.log(
        '7. customPermissions 是否包含 finance:manage:',
        hasFinanceManage
      )

      const submitData = {
        businessRole: values.businessRole,
        customPermissions: finalCustomPermissions,
        deniedPermissions: finalDeniedPermissions
      }
      console.log('8. 提交的数据:', submitData)
      console.log('9. 员工ID:', employee._id)
      console.log('10. 商家ID:', businessId)
      console.log('11. 过滤后的自定义权限:', finalCustomPermissions)
      console.log('12. 过滤后的禁用权限:', finalDeniedPermissions)
      console.log('================================')

      // 更新角色和許可權
      await updateEmployeeRole(businessId, employee._id, submitData)

      // 更新帳號狀態
      if (values.accountStatus !== employee.accountStatus) {
        await updateEmployeeStatus(
          businessId,
          employee._id,
          values.accountStatus
        )
      }

      message.success('角色更新成功')
      onSuccess()
      handleClose()
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error)
      } else {
        message.error('角色更新失敗')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    setSelectedRole(null)
    setCustomPermissions([])
    setDeniedPermissions([])
    setShowAdvanced(false)
    onClose()
  }

  const isOwner = employee?.businessRole === ROLES.OWNER
  const canModify =
    !isOwner &&
    (currentUserRole === ROLES.OWNER || currentUserRole === ROLES.MANAGER)

  const getSelectedRoleInfo = () => {
    return roles.find((r) => r.role === selectedRole)
  }

  const selectedRoleInfo = getSelectedRoleInfo()

  return (
    <Modal
      title={
        <Space>
          <span>管理員工角色</span>
          {employee && (
            <Tag color='blue'>{employee.username || employee.name}</Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key='cancel' onClick={handleClose}>
          取消
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={loading}
          onClick={handleSubmit}
          disabled={!canModify}
        >
          儲存
        </Button>
      ]}
    >
      {isOwner && (
        <Hint
          title='商家擁有者'
          tone='info'
          style={{ marginBottom: 16 }}
        >
          擁有者的角色和許可權不可修改
        </Hint>
      )}

      {!canModify && !isOwner && (
        <Hint
          title='許可權不足'
          tone='warning'
          style={{ marginBottom: 16 }}
        >
          您沒有許可權修改此員工的角色
        </Hint>
      )}

      <Form form={form} layout='vertical' disabled={!canModify || isOwner}>
        <Form.Item
          name='businessRole'
          label='選擇角色'
          rules={[{ required: true, message: '請選擇角色' }]}
        >
          <Select
            placeholder='請選擇角色'
            onChange={handleRoleChange}
            size='large'
          >
            {roles.map((role) => (
              <Option
                key={role.role}
                value={role.role}
                disabled={
                  role.role === ROLES.OWNER ||
                  (role.role === ROLES.MANAGER &&
                    currentUserRole !== ROLES.OWNER)
                }
              >
                <Space>
                  <Tag color={ROLE_COLORS[role.role]}>{role.name}</Tag>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {role.description}
                  </span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedRoleInfo && (
          <Descriptions
            bordered
            size='small'
            column={1}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label='角色名稱'>
              <RoleTag color={ROLE_COLORS[selectedRoleInfo.role]}>
                {selectedRoleInfo.name}
              </RoleTag>
            </Descriptions.Item>
            <Descriptions.Item label='角色說明'>
              {selectedRoleInfo.description}
            </Descriptions.Item>
            <Descriptions.Item label='預設許可權'>
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {selectedRoleInfo.permissions.includes('*') ? (
                  <Tag color='red'>所有許可權</Tag>
                ) : (
                  selectedRoleInfo.permissions.map((perm) => (
                    <Tag key={perm} style={{ margin: '2px' }}>
                      {PERMISSIONS_MAP[perm] || perm}
                    </Tag>
                  ))
                )}
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}

        <Divider />

        <Form.Item
          name='accountStatus'
          label='帳號狀態'
          rules={[{ required: true, message: '請選擇帳號狀態' }]}
        >
          <Select placeholder='請選擇帳號狀態'>
            {Object.keys(ACCOUNT_STATUS).map((key) => (
              <Option key={key} value={ACCOUNT_STATUS[key]}>
                <Space>
                  <Tag
                    color={
                      ACCOUNT_STATUS[key] === 'active'
                        ? 'green'
                        : ACCOUNT_STATUS[key] === 'inactive'
                        ? 'red'
                        : 'orange'
                    }
                  >
                    {ACCOUNT_STATUS_NAMES[ACCOUNT_STATUS[key]]}
                  </Tag>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {ACCOUNT_STATUS[key] === 'active' && '可以正常登入使用'}
                    {ACCOUNT_STATUS[key] === 'inactive' && '無法登入系統'}
                    {ACCOUNT_STATUS[key] === 'suspended' && '臨時暫停使用'}
                  </span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Divider />

        <div style={{ marginBottom: 16 }}>
          <Space>
            <span>高階設定</span>
            <Switch
              checked={showAdvanced}
              onChange={setShowAdvanced}
              checkedChildren='展開'
              unCheckedChildren='收起'
            />
          </Space>
        </div>

        {showAdvanced && (
          <Collapse ghost>
            <Panel header='自定義許可權（額外新增）' key='custom'>
              <Hint
                title='可以為此員工新增角色預設許可權之外的額外許可權'
                tone='info'
                style={{ marginBottom: 12 }}
              />
              <PermissionSection>
                {(() => {
                  // 获取当前角色的默认权限
                  const rolePermissions = selectedRoleInfo?.permissions || []
                  const hasAllPermissions = rolePermissions.includes('*')

                  // 从 PERMISSIONS_MAP 中过滤出可用的权限
                  const permissionsFromMap = Object.entries(
                    PERMISSIONS_MAP
                  ).filter(([key]) => {
                    if (hasAllPermissions) {
                      // 如果角色有所有权限，不显示任何自定义权限选项
                      return false
                    }
                    // 只显示不在角色默认权限中的权限
                    return !rolePermissions.includes(key)
                  })

                  // 获取已经在 customPermissions 中但不在 PERMISSIONS_MAP 中的权限
                  // 这些权限需要显示出来让用户可以取消勾选
                  const existingCustomPermissions = customPermissions.filter(
                    (perm) => {
                      // 不在角色默认权限中
                      if (rolePermissions.includes(perm)) return false
                      // 不在 PERMISSIONS_MAP 中（这些是"未知"的权限，需要显示）
                      if (!PERMISSIONS_MAP[perm]) return true
                      // 已经在 permissionsFromMap 中，不需要重复
                      return false
                    }
                  )

                  // 合并权限列表
                  const availablePermissions = [
                    ...permissionsFromMap,
                    ...existingCustomPermissions.map((perm) => [
                      perm,
                      perm // 如果不在 PERMISSIONS_MAP 中，使用权限键作为显示名称
                    ])
                  ]

                  if (availablePermissions.length === 0) {
                    return (
                      <div
                        style={{
                          padding: '16px',
                          textAlign: 'center',
                          color: '#999'
                        }}
                      >
                        {hasAllPermissions
                          ? '此角色已擁有所有許可權，無需額外新增'
                          : '沒有可用的額外許可權'}
                      </div>
                    )
                  }

                  return availablePermissions.map(([key, value]) => (
                    <PermissionItem key={key}>
                      <Checkbox
                        checked={customPermissions.includes(key)}
                        onChange={(e) => {
                          console.log('=== 自定义权限勾选变化 ===')
                          console.log('权限:', key)
                          console.log('勾选状态:', e.target.checked)
                          console.log('变化前的 customPermissions:', [
                            ...customPermissions
                          ])

                          if (e.target.checked) {
                            const newPermissions = [...customPermissions, key]
                            console.log(
                              '变化后的 customPermissions (添加):',
                              newPermissions
                            )
                            setCustomPermissions(newPermissions)
                          } else {
                            const newPermissions = customPermissions.filter(
                              (p) => p !== key
                            )
                            console.log(
                              '变化后的 customPermissions (移除):',
                              newPermissions
                            )
                            setCustomPermissions(newPermissions)
                          }
                          console.log('==========================')
                        }}
                      >
                        <Space>
                          <span>{value}</span>
                          <Tag>{key}</Tag>
                        </Space>
                      </Checkbox>
                    </PermissionItem>
                  ))
                })()}
              </PermissionSection>
            </Panel>

            <Panel header='禁用許可權（從角色許可權中移除）' key='denied'>
              <Hint
                title='可以移除角色預設擁有的某些許可權'
                tone='warning'
                style={{ marginBottom: 12 }}
              />
              <PermissionSection>
                {selectedRoleInfo &&
                  selectedRoleInfo.permissions
                    .filter((p) => p !== '*')
                    .map((perm) => (
                      <PermissionItem key={perm}>
                        <Checkbox
                          checked={deniedPermissions.includes(perm)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDeniedPermissions([...deniedPermissions, perm])
                            } else {
                              setDeniedPermissions(
                                deniedPermissions.filter((p) => p !== perm)
                              )
                            }
                          }}
                        >
                          <Space>
                            <span>{PERMISSIONS_MAP[perm] || perm}</span>
                            <Tag>{perm}</Tag>
                          </Space>
                        </Checkbox>
                      </PermissionItem>
                    ))}
              </PermissionSection>
            </Panel>
          </Collapse>
        )}
      </Form>
    </Modal>
  )
}

export default RoleManagementModal
